import { useCallback, useEffect, useMemo, useState } from 'react';
import Dropdown from '../components/Dropdown';
import Field from '../components/Field';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import SearchableSelect from '../components/SearchableSelect';
import { ErrorBanner, Toast } from '../components/Feedback';
import useForm from '../hooks/useForm';
import { api } from '../api/client';
import * as v from '../validation/rules';
import { MASTER_CODES, masterName } from '../validation/masterRules';

/**
 * The State, City, Board and Subject masters, on one screen.
 *
 * <p>Four tables that behave identically — add a row, rename it, switch it off — so they
 * are four tabs of one screen rather than four screens. Nothing is ever deleted: schools
 * and teacher profiles point at these rows, so switching one off takes it out of every
 * picker and leaves what already uses it intact.
 */

const TABS = [
  { key: 'states', label: 'States' },
  { key: 'cities', label: 'Cities' },
  { key: 'boards', label: 'Boards' },
  { key: 'subjects', label: 'Subjects' },
  { key: 'designations', label: 'Designations' },
  { key: 'classLevels', label: 'Class Levels' },
];

export default function MastersPage() {
  const [tab, setTab] = useState('states');
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Masters</h1>
          <p>
            The lists every other screen picks from. A row is switched off rather than
            deleted, so the schools and teachers already filed under it stay intact.
          </p>
        </div>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      <div className="filter-chips" style={{ marginBottom: 'var(--s1)' }} role="tablist">
        {TABS.map((entry) => (
          <button
            type="button"
            key={entry.key}
            role="tab"
            className="chip"
            aria-pressed={tab === entry.key}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'states' && <StatesTab onError={setError} onDone={setToast} />}
      {tab === 'cities' && <CitiesTab onError={setError} onDone={setToast} />}
      {tab === 'boards' && <BoardsTab onError={setError} onDone={setToast} />}
      {tab === 'subjects' && <SubjectsTab onError={setError} onDone={setToast} />}
      {tab === 'designations' && <DesignationsTab onError={setError} onDone={setToast} />}
      {tab === 'classLevels' && <ClassLevelsTab onError={setError} onDone={setToast} />}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}

// ── states ────────────────────────────────────────────────────────────────

function StatesTab({ onError, onDone }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.adminListStates().then(setRows).catch((err) => {
      setRows([]);
      onError(err);
    });
  }, [onError]);

  useEffect(load, [load]);

  return (
    <MasterTable
      addLabel="Add state"
      onAdd={() => setEditing({})}
      rows={rows}
      empty="No states yet."
      columns={['State', 'Code', 'Cities', 'Status', '']}
      renderRow={(row) => (
        <tr key={row.id}>
          <td>{row.name}</td>
          <td className="mono">{row.code ?? '—'}</td>
          <td>{row.cityCount.toLocaleString()}</td>
          <td><ActiveBadge active={row.active} /></td>
          <td style={{ textAlign: 'right' }}>
            <RowActions
              row={row}
              onEdit={() => setEditing(row)}
              onToggle={() =>
                api
                  .adminSetStateActive(row.id, !row.active)
                  .then(() => {
                    onDone(`${row.name} is now ${row.active ? 'inactive' : 'active'}`);
                    load();
                  })
                  .catch(onError)
              }
            />
          </td>
        </tr>
      )}
      modal={
        editing && (
          <MasterModal
            title={editing.id ? 'Edit state' : 'Add state'}
            subtitle="Its cities follow it in and out of every picker"
            initialValues={{ name: editing.name ?? '', code: editing.code ?? '' }}
            validators={{ name: masterName('State', 100), code: v.optional(() => null) }}
            serverCodes={MASTER_CODES}
            onClose={() => setEditing(null)}
            onSave={(values) => {
              const body = { name: values.name.trim(), code: values.code.trim() || null };
              const saved = editing.id
                ? api.adminUpdateState(editing.id, body)
                : api.adminCreateState(body);
              return saved.then(() => {
                setEditing(null);
                onDone(`${body.name} saved`);
                load();
              });
            }}
          >
            {(form) => (
              <>
                <Field label="State name" id="name" required error={form.errorFor('name')}>
                  <input className="input" maxLength={100} {...form.field('name')} />
                </Field>
                <Field
                  label="Code"
                  id="code"
                  hint="Optional — the two-letter code, where the state has one"
                  error={form.errorFor('code')}
                >
                  <input className="input" maxLength={10} placeholder="KA" {...form.field('code')} />
                </Field>
              </>
            )}
          </MasterModal>
        )
      }
    />
  );
}

// ── cities ────────────────────────────────────────────────────────────────

function CitiesTab({ onError, onDone }) {
  const [page, setPage] = useState({ items: [], page: 0, totalItems: 0, hasNext: false });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [states, setStates] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.adminListStates().then(setStates).catch(() => setStates([]));
  }, []);

  const load = useCallback(
    (pageNumber = 0) => {
      setLoading(true);
      api
        .adminListCities({ q: search, stateId: stateFilter || undefined, page: pageNumber, size: 20 })
        .then(setPage)
        .catch(onError)
        .finally(() => setLoading(false));
    },
    [search, stateFilter, onError],
  );

  useEffect(() => {
    // Debounced so typing in the search box sends one request, not one per keystroke.
    const handle = setTimeout(() => load(0), 250);
    return () => clearTimeout(handle);
  }, [load]);

  const stateOptions = useMemo(
    () => states.map((state) => ({ id: state.id, name: state.name })),
    [states],
  );

  return (
    <>
      <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
        <div className="toolbar">
          <input
            className="input"
            style={{ flex: '1 1 220px' }}
            placeholder="Search cities"
            aria-label="Search cities"
            maxLength={120}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div style={{ width: 220 }}>
            <Dropdown
              ariaLabel="Filter by state"
              options={states}
              value={stateFilter}
              onChange={setStateFilter}
              placeholder="All states"
            />
          </div>
        </div>
      </section>

      <MasterTable
        addLabel="Add city"
        onAdd={() => setEditing({})}
        rows={loading ? null : page.items}
        empty="No city matches these filters."
        columns={['City', 'State', 'Status', '']}
        footer={
          <div className="toolbar" style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'space-between' }}>
            <span className="muted small">
              {page.totalItems.toLocaleString()} {page.totalItems === 1 ? 'city' : 'cities'}
            </span>
            <span style={{ display: 'flex', gap: 'var(--s0)' }}>
              <button type="button" className="btn btn-secondary btn-sm" disabled={page.page === 0}
                      onClick={() => load(page.page - 1)}>
                Previous
              </button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={!page.hasNext}
                      onClick={() => load(page.page + 1)}>
                Next
              </button>
            </span>
          </div>
        }
        renderRow={(row) => (
          <tr key={row.id}>
            <td>{row.name}</td>
            <td className="muted">{row.stateName}</td>
            <td><ActiveBadge active={row.active} /></td>
            <td style={{ textAlign: 'right' }}>
              <RowActions
                row={row}
                onEdit={() => setEditing(row)}
                onToggle={() =>
                  api
                    .adminSetCityActive(row.id, !row.active)
                    .then(() => {
                      onDone(`${row.name} is now ${row.active ? 'inactive' : 'active'}`);
                      load(page.page);
                    })
                    .catch(onError)
                }
              />
            </td>
          </tr>
        )}
        modal={
          editing && (
            <CityModal
              city={editing}
              stateOptions={stateOptions}
              onClose={() => setEditing(null)}
              onSaved={(name) => {
                setEditing(null);
                onDone(`${name} saved`);
                load(page.page);
              }}
            />
          )
        }
      />
    </>
  );
}

/**
 * Separate from the generic modal because a city needs a picker for its state, and a
 * picker's value lives outside useForm — which validates text fields, not objects.
 */
function CityModal({ city, stateOptions, onClose, onSaved }) {
  const [state, setState] = useState(
    city.stateId ? { id: city.stateId, name: city.stateName } : null,
  );
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(null);

  const form = useForm({
    initialValues: { name: city.name ?? '' },
    validators: { name: masterName('City', 120) },
  });

  const submit = form.handleSubmit(async (values) => {
    setTouched(true);
    if (!state?.id) return;
    const body = { name: values.name.trim(), stateId: state.id };
    setError(null);
    try {
      if (city.id) await api.adminUpdateCity(city.id, body);
      else await api.adminCreateCity(body);
      onSaved(body.name);
    } catch (err) {
      if (!form.applyServerErrors(err, MASTER_CODES)) setError(err);
    }
  });

  const stateError = touched && !state?.id ? 'State is required' : null;

  return (
    <Modal
      title={city.id ? 'Edit city' : 'Add city'}
      subtitle="A city name is unique within its state, not across the country"
      width={560}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="city-form" className="btn btn-primary" disabled={form.submitting}>
            <Icon name="check" size={15} />
            {form.submitting ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="city-form" {...form.formProps} onSubmit={submit} className="stack">
        <ErrorBanner error={error} onDismiss={() => setError(null)} />
        <Field label="City name" id="name" required error={form.errorFor('name')}>
          <input className="input" maxLength={120} {...form.field('name')} />
        </Field>
        <Field label="State" id="cityState" required error={stateError}>
          <SearchableSelect
            id="cityState"
            value={state}
            onChange={(chosen) => {
              setState(chosen);
              setTouched(true);
            }}
            options={stateOptions}
            placeholder="Search states"
            error={stateError}
          />
        </Field>
      </form>
    </Modal>
  );
}

// ── boards and subjects ───────────────────────────────────────────────────

function BoardsTab({ onError, onDone }) {
  return (
    <SimpleMasterTab
      onError={onError}
      onDone={onDone}
      noun="board"
      addLabel="Add board"
      subtitle="Renaming a board keeps its code, so nothing already filed under it moves"
      columns={['Board', 'Code', 'Order', 'Status', '']}
      list={api.adminListBoards}
      create={api.adminCreateBoard}
      update={api.adminUpdateBoard}
      setActive={api.adminSetBoardActive}
      nameLimit={100}
      cells={(row) => (
        <>
          <td>{row.name}</td>
          <td className="mono">{row.code}</td>
          <td>{row.sortOrder}</td>
        </>
      )}
    />
  );
}

function SubjectsTab({ onError, onDone }) {
  return (
    <SimpleMasterTab
      onError={onError}
      onDone={onDone}
      noun="subject"
      addLabel="Add subject"
      subtitle="What a teacher can pick on the registration screen"
      columns={['Subject', 'Order', 'Status', '']}
      list={api.adminListSubjects}
      create={api.adminCreateSubject}
      update={api.adminUpdateSubject}
      setActive={api.adminSetSubjectActive}
      nameLimit={80}
      cells={(row) => (
        <>
          <td>{row.name}</td>
          <td>{row.sortOrder}</td>
        </>
      )}
    />
  );
}

function DesignationsTab({ onError, onDone }) {
  return (
    <SimpleMasterTab
      onError={onError}
      onDone={onDone}
      noun="designation"
      addLabel="Add designation"
      subtitle="What a teacher is called at their school, on the registration form"
      columns={['Designation', 'Order', 'Status', '']}
      list={api.adminListDesignations}
      create={api.adminCreateDesignation}
      update={api.adminUpdateDesignation}
      setActive={api.adminSetDesignationActive}
      nameLimit={80}
      cells={(row) => (
        <>
          <td>{row.name}</td>
          <td>{row.sortOrder}</td>
        </>
      )}
    />
  );
}

function ClassLevelsTab({ onError, onDone }) {
  return (
    <SimpleMasterTab
      onError={onError}
      onDone={onDone}
      noun="class level"
      addLabel="Add class level"
      subtitle="The bands a teacher picks under Classes Handling"
      columns={['Class level', 'Order', 'Status', '']}
      list={api.adminListClassLevels}
      create={api.adminCreateClassLevel}
      update={api.adminUpdateClassLevel}
      setActive={api.adminSetClassLevelActive}
      nameLimit={40}
      cells={(row) => (
        <>
          <td>{row.name}</td>
          <td>{row.sortOrder}</td>
        </>
      )}
    />
  );
}

/** Boards and subjects differ only in their columns, so they share everything else. */
function SimpleMasterTab({
  onError, onDone, noun, addLabel, subtitle, columns, list, create, update, setActive, nameLimit, cells,
}) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    list().then(setRows).catch((err) => {
      setRows([]);
      onError(err);
    });
  }, [list, onError]);

  useEffect(load, [load]);

  const label = noun.charAt(0).toUpperCase() + noun.slice(1);

  return (
    <MasterTable
      addLabel={addLabel}
      onAdd={() => setEditing({})}
      rows={rows}
      empty={`No ${noun}s yet.`}
      columns={columns}
      renderRow={(row) => (
        <tr key={row.id}>
          {cells(row)}
          <td><ActiveBadge active={row.active} /></td>
          <td style={{ textAlign: 'right' }}>
            <RowActions
              row={row}
              onEdit={() => setEditing(row)}
              onToggle={() =>
                setActive(row.id, !row.active)
                  .then(() => {
                    onDone(`${row.name} is now ${row.active ? 'inactive' : 'active'}`);
                    load();
                  })
                  .catch(onError)
              }
            />
          </td>
        </tr>
      )}
      modal={
        editing && (
          <MasterModal
            title={editing.id ? `Edit ${noun}` : addLabel}
            subtitle={subtitle}
            initialValues={{
              name: editing.name ?? '',
              sortOrder: editing.sortOrder == null ? '' : String(editing.sortOrder),
            }}
            validators={{ name: masterName(label, nameLimit), sortOrder: sortOrderRule }}
            serverCodes={MASTER_CODES}
            onClose={() => setEditing(null)}
            onSave={(values) => {
              const body = {
                name: values.name.trim(),
                sortOrder: values.sortOrder === '' ? null : Number(values.sortOrder),
              };
              const saved = editing.id ? update(editing.id, body) : create(body);
              return saved.then(() => {
                setEditing(null);
                onDone(`${body.name} saved`);
                load();
              });
            }}
          >
            {(form) => (
              <>
                <Field label={`${label} name`} id="name" required error={form.errorFor('name')}>
                  <input className="input" maxLength={nameLimit} {...form.field('name')} />
                </Field>
                <Field
                  label="Sort order"
                  id="sortOrder"
                  hint="Lower comes first in every picker; leave blank for the default"
                  error={form.errorFor('sortOrder')}
                >
                  <input className="input" inputMode="numeric" maxLength={4} {...form.field('sortOrder')} />
                </Field>
              </>
            )}
          </MasterModal>
        )
      }
    />
  );
}

const sortOrderRule = v.optional((value) =>
  /^\d{1,4}$/.test(String(value).trim()) ? null : 'Sort order must be a whole number up to 9999',
);

// ── shared pieces ─────────────────────────────────────────────────────────

function MasterTable({ addLabel, onAdd, rows, columns, renderRow, empty, footer, modal }) {
  return (
    <>
      <section className="card">
        <div className="toolbar" style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={onAdd}>
            <Icon name="plus" size={15} />
            {addLabel}
          </button>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <th key={column || `actions-${index}`} aria-label={column ? undefined : 'Actions'}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{(rows ?? []).map(renderRow)}</tbody>
          </table>
        </div>
        {rows === null && <div className="empty">Loading…</div>}
        {rows !== null && rows.length === 0 && <div className="empty">{empty}</div>}
        {footer}
      </section>
      {modal}
    </>
  );
}

function ActiveBadge({ active }) {
  return (
    <span className={`badge${active ? ' badge-ok' : ''}`}>{active ? 'Active' : 'Inactive'}</span>
  );
}

function RowActions({ row, onEdit, onToggle }) {
  return (
    <span style={{ display: 'inline-flex', gap: 'var(--s0)' }}>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onEdit}>
        Edit
      </button>
      <button
        type="button"
        className={`btn btn-sm ${row.active ? 'btn-danger' : 'btn-secondary'}`}
        onClick={onToggle}
      >
        {row.active ? 'Switch off' : 'Switch on'}
      </button>
    </span>
  );
}

/** The add/edit dialog every tab but Cities uses; the fields come in as a render prop. */
function MasterModal({ title, subtitle, initialValues, validators, serverCodes, onClose, onSave, children }) {
  const [error, setError] = useState(null);
  const form = useForm({ initialValues, validators });

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onSave(values);
    } catch (err) {
      if (!form.applyServerErrors(err, serverCodes)) setError(err);
    }
  });

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      width={560}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="master-form" className="btn btn-primary" disabled={form.submitting}>
            <Icon name="check" size={15} />
            {form.submitting ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="master-form" {...form.formProps} onSubmit={submit} className="stack">
        <ErrorBanner error={error} onDismiss={() => setError(null)} />
        {children(form)}
      </form>
    </Modal>
  );
}
